import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { StateBadge } from '@/components/platform/StateBadge';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import { IntegrationsArea } from './IntegrationsArea';
import type {
  ConnectionHealthView,
  PlatformConnection,
  PlatformHealthSnapshot,
  PlatformIncident,
} from '@/types/platform';
import { formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * Everything in this area that is currently wrong, in one place.
 *
 * FOUR SEPARATE REQUESTS, FOUR SEPARATE FAILURE STATES. Connections, the platform
 * health snapshot and the open incident list come from different endpoints with
 * different deployment schedules. They are loaded independently so that one
 * missing route leaves three working panels rather than an empty screen — and each
 * panel says which kind of failure it hit, because "not deployed", "not permitted"
 * and "unreachable" send an operator somewhere different.
 *
 * WHAT THIS SCREEN DELIBERATELY DOES NOT CLAIM. Credential EXPIRY is not tracked
 * anywhere: a connection stores a vault pointer, and nothing in this platform
 * knows when the value behind that pointer stops working. A panel promising to
 * warn about expiring credentials would be a promise no data supports, and the
 * first credential that expired silently would have been the one it was built to
 * catch. So the credential panel reports what is actually knowable — which
 * connections authenticate but have no reference recorded at all — and says
 * plainly that expiry is not among them.
 */

export function IntegrationHealthPage() {
  const { t } = useTranslation();

  return (
    <IntegrationsArea
      active="health"
      title={t('platform.integrationHealth.title')}
      lead={t('platform.integrationHealth.lead')}
    >
      <ConnectionChecksPanel />
      <CredentialReferencesPanel />
      <PlatformSignalsPanel />
      <OpenIncidentsPanel />
    </IntegrationsArea>
  );
}

/* ─── Failed and stale checks ─── */

/**
 * The server's own health view, not one assembled here.
 *
 * `reporting` is the server's judgement and is not a function of `status` alone:
 * a connection with no address at all is UNKNOWN, one nothing has ever probed is
 * NOT_REPORTING, and neither of those is a fault. Recomputing that in the browser
 * would put a second copy of the rule somewhere it can drift, and the direction it
 * would drift in is towards calling unfinished work an outage.
 */
function ConnectionChecksPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const fetcher = useCallback(() => platformApi.connections.health(), []);
  const { state, reload } = useAsyncData<ConnectionHealthView[]>('health-connections', fetcher);

  return (
    <Panel
      title={t('platform.integrationHealth.checksTitle')}
      icon="pulse"
      emphasis="attention"
      actions={
        <button type="button" className={styles.secondaryBtn} onClick={reload}>
          <Icon name="refresh" size={15} />
          {t('platform.dashboard.refresh')}
        </button>
      }
    >
      <p className={styles.fieldHint}>{t('platform.integrationHealth.checksLead')}</p>
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => needsAttention(data).length === 0}
        emptyTitle={t('platform.integrationHealth.checksEmptyTitle')}
        emptyBody={t('platform.integrationHealth.checksEmptyBody')}
      >
        {(data) => (
          <ul className={styles.rowList}>
            {needsAttention(data).map((view) => (
              <li key={view.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <p className={styles.rowTitle}>
                    <Link
                      className={styles.rowTitleLink}
                      to={`/admin/integrations/connections/${encodeURIComponent(view.id)}`}
                    >
                      {view.displayName}
                    </Link>
                  </p>
                  <p className={styles.rowMetaLine}>
                    {t(`platform.environment.${view.environment}`)}
                    {` · ${
                      view.lastCheckedAt
                        ? t('platform.integrationHealth.checkedWhen', {
                            when: formatRelative(view.lastCheckedAt, locale),
                          })
                        : t('platform.connections.neverChecked')
                    }`}
                  </p>
                  {/* The diagnosis in words. A badge says which state it is in;
                      this says what is actually wrong and who should act. */}
                  <p className={styles.rowDetail}>{t(diagnosisKey(view))}</p>
                  {/* Straight from the server: what would have to exist for this
                      connection to be able to answer at all. Without it,
                      "nothing reporting" is a dead end rather than a task. */}
                  {view.wouldRequire && (
                    <p className={styles.rowDetail}>
                      {t('platform.operations.wouldRequire', { detail: view.wouldRequire })}
                    </p>
                  )}
                  {view.lastError && <p className={styles.rowDetailError}>{view.lastError}</p>}
                </div>
                <div className={styles.rowAside}>
                  <StateBadge kind="integration" value={view.status} size="sm" />
                  <span className={styles.infoTag}>
                    {t(`platform.operations.state.${view.reporting}`, { defaultValue: view.reporting })}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>
    </Panel>
  );
}

/**
 * Which connections an operator should look at, and why.
 *
 * "Never succeeded" is included alongside failing and degraded because a
 * connection that has never once answered is not a neutral, unstarted thing — it
 * is work that was begun and left, and it stays invisible forever if the only
 * things listed are the ones that used to work. Disabled is excluded: that is a
 * decision somebody made, not a fault, and putting decisions in an attention list
 * trains people to ignore the list.
 */
function needsAttention(views: readonly ConnectionHealthView[]): ConnectionHealthView[] {
  return views.filter(
    (view) =>
      view.status !== 'DISABLED' &&
      (view.reporting === 'UNHEALTHY' || view.reporting === 'DEGRADED' || !view.lastSuccessfulAt),
  );
}

function diagnosisKey(view: ConnectionHealthView): string {
  if (view.reporting === 'UNKNOWN') {
    return 'platform.integrationHealth.diagnosisNoAddress';
  }
  if (!view.lastCheckedAt) {
    return 'platform.integrationHealth.diagnosisNeverChecked';
  }
  if (!view.lastSuccessfulAt) {
    return 'platform.integrationHealth.diagnosisNeverSucceeded';
  }
  if (view.reporting === 'DEGRADED') {
    return 'platform.integrationHealth.diagnosisDegraded';
  }
  return 'platform.integrationHealth.diagnosisFailed';
}

/* ─── Credential references ─── */

function CredentialReferencesPanel() {
  const { t } = useTranslation();
  const fetcher = useCallback(() => platformApi.connections.list(), []);
  const { state, reload } = useAsyncData<PlatformConnection[]>('health-credentials', fetcher);

  return (
    <Panel title={t('platform.integrationHealth.credentialsTitle')} icon="noAccess">
      {/*
        Said before the list, not after it. An empty list here means "no connection
        is missing a reference", which is a much weaker statement than "no
        credential is about to expire" — and the second is what somebody will read
        it as unless told otherwise.
      */}
      <div className={styles.warningNotice} role="note">
        <Icon name="info" size={16} aria-hidden="true" />
        <div>
          <p className={styles.warningTitle}>{t('platform.integrationHealth.expiryUnknownTitle')}</p>
          <p className={styles.warningBody}>{t('platform.integrationHealth.expiryUnknownBody')}</p>
        </div>
      </div>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => missingCredential(data).length === 0}
        emptyTitle={t('platform.integrationHealth.credentialsEmptyTitle')}
        emptyBody={t('platform.integrationHealth.credentialsEmptyBody')}
      >
        {(data) => (
          <ul className={styles.rowList}>
            {missingCredential(data).map((connection) => (
              <li key={connection.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <p className={styles.rowTitle}>
                    <Link
                      className={styles.rowTitleLink}
                      to={`/admin/integrations/connections/${encodeURIComponent(connection.id)}`}
                    >
                      {connection.displayName}
                    </Link>
                  </p>
                  <p className={styles.rowMetaLine}>
                    {t(`platform.environment.${connection.environment}`)}
                    {` · ${t(`platform.authentication.${connection.authenticationType}`, {
                      defaultValue: connection.authenticationType,
                    })}`}
                  </p>
                  <p className={styles.rowDetail}>{t('platform.integrationHealth.credentialMissing')}</p>
                </div>
                <div className={styles.rowAside}>
                  <span className={styles.warnTag}>{t('platform.connections.credentialNone')}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>
    </Panel>
  );
}

/** A connection that authenticates but names no vault path cannot work at all. */
function missingCredential(connections: readonly PlatformConnection[]): PlatformConnection[] {
  return connections.filter(
    (connection) =>
      connection.status !== 'DISABLED' &&
      connection.authenticationType !== 'NONE' &&
      !connection.credentialVaultReference,
  );
}

/* ─── Platform signals ─── */

/**
 * Sync failures, event backlog and the rest of PA-13's operational signals.
 *
 * The `honesty` block is rendered rather than swallowed. It lists which signals
 * this service actually measures, which are silent and which cannot be measured at
 * all — without it a screen showing four green signals over a service that
 * instruments four of its twenty subsystems is a clean bill of health nobody
 * earned.
 */
function PlatformSignalsPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const fetcher = useCallback(() => platformApi.operations.health(), []);
  const { state, reload } = useAsyncData<PlatformHealthSnapshot>('health-signals', fetcher);

  return (
    <Panel title={t('platform.integrationHealth.signalsTitle')} icon="pulse">
      <p className={styles.fieldHint}>{t('platform.integrationHealth.signalsLead')}</p>
      <AsyncPanel state={state} onRetry={reload}>
        {(snapshot) => (
          <>
            {snapshot.signals.length === 0 ? (
              <p className={styles.sectionEmpty} role="status">
                {t('platform.operations.noSignals')}
              </p>
            ) : (
              <ul className={styles.rowList}>
                {snapshot.signals.map((signal) => (
                  <li key={signal.key} className={styles.row}>
                    <div className={styles.rowMain}>
                      <p className={styles.rowTitle}>{signal.title}</p>
                      <p className={styles.rowMetaLine}>{signal.basis}</p>
                      {signal.wouldRequire && (
                        <p className={styles.rowDetail}>
                          {t('platform.operations.wouldRequire', { detail: signal.wouldRequire })}
                        </p>
                      )}
                    </div>
                    <div className={styles.rowAside}>
                      <span className={styles.infoTag}>
                        {t(`platform.operations.state.${signal.state}`, { defaultValue: signal.state })}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className={styles.warningNotice} role="note">
              <Icon name="info" size={16} aria-hidden="true" />
              <div>
                <p className={styles.warningTitle}>{t('platform.operations.honestyTitle')}</p>
                <p className={styles.warningBody}>{snapshot.honesty.statement}</p>
                <p className={styles.rowMetaLine}>
                  {t('platform.integrationHealth.honestyCounts', {
                    measured: snapshot.honesty.measuredHere.length,
                    silent: snapshot.honesty.notReporting.length,
                    unmeasurable: snapshot.honesty.notMeasurable.length,
                  })}
                </p>
              </div>
            </div>

            {snapshot.generatedAt && (
              <p className={styles.generatedAt}>
                {t('platform.integrationHealth.generatedAt', {
                  when: formatDateTime(snapshot.generatedAt, locale),
                })}
              </p>
            )}
          </>
        )}
      </AsyncPanel>
    </Panel>
  );
}

/* ─── Incidents ─── */

function OpenIncidentsPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const fetcher = useCallback(() => platformApi.operations.incidents({ openOnly: true }), []);
  const { state, reload } = useAsyncData<PlatformIncident[]>('health-incidents', fetcher);

  return (
    <Panel
      title={t('platform.integrationHealth.incidentsTitle')}
      icon="warning"
      actions={
        <Link className={styles.panelLink} to="/admin/operations">
          {t('platform.integrationHealth.incidentsManage')}
        </Link>
      }
    >
      <p className={styles.fieldHint}>{t('platform.integrationHealth.incidentsLead')}</p>
      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={(data) => data.length === 0}
        emptyTitle={t('platform.integrationHealth.incidentsEmpty')}
      >
        {(data) => (
          <ul className={styles.rowList}>
            {data.map((incident) => (
              <li key={incident.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <p className={styles.rowTitle}>{incident.title}</p>
                  <p className={styles.rowMetaLine}>
                    {incident.reference}
                    {` · ${t(`platform.operations.incidentStatus.${incident.status}`, {
                      defaultValue: incident.status,
                    })}`}
                    {` · ${formatDateTime(incident.detectedAt, locale)}`}
                  </p>
                  {incident.summary && <p className={styles.rowDetail}>{incident.summary}</p>}
                </div>
                <div className={styles.rowAside}>
                  <span className={incident.severity === 'MINOR' ? styles.infoTag : styles.warningTag}>
                    {t(`platform.operations.severity.${incident.severity}`, {
                      defaultValue: incident.severity,
                    })}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </AsyncPanel>
    </Panel>
  );
}
